// Regras puras das telas de autenticação (Login e Cadastro): força da senha,
// normalização do código OTP e o passo atual do cadastro.
// Sem React — testado em __tests__/authRules.test.js.
import { i18n } from "../../translate/i18n";

// ---- OTP -------------------------------------------------------------
// Os campos de 6 dígitos do mockup: só dígitos, no máximo `length`.
export const sanitizeOtp = (value, length = 6) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(0, length);

// Divide o código nas caixas, preenchendo com "" o que falta.
export const otpDigits = (value, length = 6) => {
  const clean = sanitizeOtp(value, length);
  return Array.from({ length }, (_, i) => clean[i] || "");
};

// ---- Força da senha --------------------------------------------------
// Quatro barras no mockup. A pontuação é intencionalmente simples e
// explicável (comprimento + variedade), não uma estimativa de entropia:
// o backend é quem impõe o mínimo real.
export const PASSWORD_LEVELS = Object.freeze([
  { score: 0, tone: "toneNeutral", labelKey: "signup.password.empty" },
  { score: 1, tone: "toneError", labelKey: "signup.password.weak" },
  { score: 2, tone: "toneWarning", labelKey: "signup.password.fair" },
  { score: 3, tone: "toneInfo", labelKey: "signup.password.good" },
  { score: 4, tone: "toneSuccess", labelKey: "signup.password.strong" },
]);

export const passwordStrength = (password) => {
  const value = String(password || "");
  if (!value) return { ...PASSWORD_LEVELS[0], label: i18n.t(PASSWORD_LEVELS[0].labelKey) };

  let points = 0;
  if (value.length >= 8) points += 1;
  if (value.length >= 12) points += 1;
  if (/[a-z]/.test(value) && /[A-Z]/.test(value)) points += 1;
  if (/\d/.test(value)) points += 1;
  if (/[^A-Za-z0-9]/.test(value)) points += 1;

  // 5 pontos possíveis comprimidos em 4 barras; qualquer senha não vazia
  // acende pelo menos uma, senão o medidor parece quebrado ao digitar.
  const score = Math.max(1, Math.min(4, points));
  const level = PASSWORD_LEVELS[score];
  return { ...level, label: i18n.t(level.labelKey) };
};

// ---- Passos do cadastro ---------------------------------------------
// O mockup mostra três passos no painel esquerdo. Não é um wizard: o
// formulário é único, e o passo "atual" é o primeiro grupo ainda incompleto.
export const SIGNUP_STEPS = Object.freeze([
  { n: 1, titleKey: "signup.steps.company.title", descKey: "signup.steps.company.desc" },
  { n: 2, titleKey: "signup.steps.owner.title", descKey: "signup.steps.owner.desc" },
  { n: 3, titleKey: "signup.steps.plan.title", descKey: "signup.steps.plan.desc" },
]);

const filled = (v) => String(v || "").trim().length > 0;

// Devolve os três passos com estado "done" | "current" | "upcoming".
export const signupStepStates = (values = {}) => {
  const done = [
    filled(values.document) && filled(values.companyName),
    filled(values.name) && filled(values.phone) && filled(values.email) && filled(values.password),
    filled(values.planId),
  ];
  const currentIndex = done.findIndex((ok) => !ok);
  return SIGNUP_STEPS.map((step, i) => ({
    ...step,
    title: i18n.t(step.titleKey),
    desc: i18n.t(step.descKey),
    state: done[i] ? "done" : i === currentIndex ? "current" : "upcoming",
  }));
};

// ---- Documento -------------------------------------------------------
export const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

// "cpf" com 11 dígitos, "cnpj" com 14, null enquanto incompleto.
export const documentKind = (value) => {
  const digits = onlyDigits(value);
  if (digits.length === 11) return "cpf";
  if (digits.length === 14) return "cnpj";
  return null;
};

// Máscara de exibição — a validação real continua no backend.
export const formatDocument = (value) => {
  const d = onlyDigits(value).slice(0, 14);
  if (d.length <= 11) {
    return d
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3-$4");
  }
  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3/$4")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, "$1.$2.$3/$4-$5");
};

// ---- Redefinição de senha -------------------------------------------
// O mínimo real é do backend (CreateUserService usa 5); repetido aqui só para
// travar o botão antes do round-trip.
export const MIN_PASSWORD_LENGTH = 5;

// Não apara nenhum dos lados: espaço faz parte da senha, e o backend grava
// exatamente o que recebe.
export const senhasConferem = (senha, confirmacao) => {
  const a = String(senha ?? "");
  const b = String(confirmacao ?? "");
  if (a.length < MIN_PASSWORD_LENGTH) return false;
  return a === b;
};

export const RESEND_COOLDOWN_SECONDS = 60;

// Quanto falta para poder pedir outro código. Espelha o throttle de 60s do
// SendPasswordResetOtpService: sem esse eco, o usuário só descobriria a trava
// clicando e tomando erro 429.
export const segundosParaReenviar = (
  enviadoEm,
  agora,
  cooldownSegundos = RESEND_COOLDOWN_SECONDS
) => {
  if (enviadoEm === null || enviadoEm === undefined) return 0;
  const decorrido = (agora - enviadoEm) / 1000;
  const restante = Math.ceil(cooldownSegundos - decorrido);
  return Math.min(cooldownSegundos, Math.max(0, restante));
};
